import './ProjectThumbItem.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faDrawPolygon, faEllipsisV, faExclamationTriangle, faGlasses, faImages, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button, Dropdown, IconButton, ListDivider, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useHistory } from 'react-router';
import { S_Link2 } from '../../../../../design/links/S_Link2';
import { S_ClampLines } from '../../../../../design/misc/S_ClampLines';
import { TrashIcon } from '../../../../../design/static/icons/TrashIcon';
import { SpaceBetweenLayout } from '../../../../../modules/common/layouts/spaceBetweenLayout/SpaceBetweenLayout';
import { DropdownLinkItem } from '../../../../components/DropdownLinkItem';
import { EnableFor } from '../../../../containers/EnableForRole';
import { ProjectRole } from '../../../../models/userRole.model';
import { Home } from '../../../../routes/config/Home';
import { useAnnotationPermissions } from '../../../annotation/annotation.permissions';
import { useClarificationsPermissions } from '../../../annotation/submodules/clarifications/Clarifications.permissions';
import { useProjectPermissions } from '../../projects.permissions';

library.add(faEllipsisV, faUser, faImages, faUsers, faDrawPolygon, faGlasses, faExclamationTriangle);

interface IProjectListItemProps {
  id: string;
  name: string;
  description: string;
  imageCount: number;
  usersCount: number;
  myRejectedCount: number;
  workspaceId: string;
  unansweredClarificationsCount: number;
  projectRole: ProjectRole;
  onProjectDelete(projectId: string): Promise<void>;
  onGoToClarifications?(projectId: string): Promise<void>;
}

export const ProjectThumbItem = observer((props: IProjectListItemProps): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();
  const projectsPermissions = useProjectPermissions();
  const clarificationsPermissions = useClarificationsPermissions();
  const annotationPermissions = useAnnotationPermissions();

  const hasAccess = projectsPermissions.canView().allowed;

  return (
    <div className='project-thumb'>
      <div className='project-thumb-head'>
        <div className='name'>
          {!hasAccess ? (
            <S_ClampLines
              text={props.name}
              lines={2}
            />
          ) : (
            <S_Link2
              color='text'
              to={Home.Projects.Details.Overview.withParams({ projectId: props.id, workspaceId: props.workspaceId })}
            >
              <S_ClampLines
                text={props.name}
                lines={2}
              />
            </S_Link2>
          )}
        </div>
        <Dropdown>
          <MenuButton
            slots={{ root: IconButton }}
            slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </MenuButton>
          <Menu placement='bottom-end'>
            <EnableFor
              hasPermission={projectsPermissions.canView()}
              hideWhenNoAccess
            >
              <DropdownLinkItem linkTo={Home.Projects.Details.Overview.withParams({ projectId: props.id, workspaceId: props.workspaceId })}>
                {t('project_details')}
              </DropdownLinkItem>
            </EnableFor>
            {projectsPermissions.canView().allowed && annotationPermissions.canAnnotate(props.id).allowed && <ListDivider />}
            <EnableFor
              hasPermission={annotationPermissions.canAnnotate(props.id)}
              hideWhenNoAccess
            >
              <DropdownLinkItem linkTo={Home.Annotations.Create.withParams({ projectId: props.id, workspaceId: props.workspaceId })}>
                {t('start_annotation')}
              </DropdownLinkItem>
            </EnableFor>
            <EnableFor
              hasPermission={annotationPermissions.canReview(props.id)}
              hideWhenNoAccess
            >
              <DropdownLinkItem linkTo={Home.Annotations.Review.withParams({ projectId: props.id, workspaceId: props.workspaceId })}>
                {t('start_review')}
              </DropdownLinkItem>
            </EnableFor>
            <EnableFor
              hasPermission={projectsPermissions.canEdit()}
              hideWhenNoAccess
            >
              <ListDivider />
              <MenuItem
                onClick={(): void => {
                  void props.onProjectDelete(props.id);
                }}
              >
                <TrashIcon /> {t('delete_project')}
              </MenuItem>
            </EnableFor>
          </Menu>
        </Dropdown>
      </div>
      <div className='info'>
        {props.description && (
          <div className='description'>
            <S_ClampLines
              text={props.description}
              lines={2}
            />
          </div>
        )}
        <SpaceBetweenLayout>
          <div>
            <FontAwesomeIcon
              icon={faImages}
              className='info-icon'
            />{' '}
            <span>{props.imageCount}</span> {t('images')}
          </div>
          <EnableFor
            hasPermission={projectsPermissions.canView()}
            hideWhenNoAccess
          >
            <div>
              <FontAwesomeIcon
                icon={faUsers}
                className='info-icon'
              />{' '}
              <span>{props.usersCount}</span> {t('users')}
            </div>
          </EnableFor>
        </SpaceBetweenLayout>
        {props.myRejectedCount > 0 && (
          <div className='warning'>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <strong>{props.myRejectedCount}</strong>
            &nbsp;
            {t('rejected_annotations_warning', { count: props.myRejectedCount })}
          </div>
        )}
        <EnableFor
          hasPermission={clarificationsPermissions.canSeeClarifications()}
          hideWhenNoAccess
        >
          {props.unansweredClarificationsCount > 0 && (
            <div
              className={`warning ${props.onGoToClarifications ? 'link' : ''}`}
              onClick={() => void props.onGoToClarifications?.(props.id)}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <strong>{props.unansweredClarificationsCount}</strong>
              &nbsp;
              {t('clarifications.unanswered_clarifications_warning', { count: props.unansweredClarificationsCount })}
            </div>
          )}
        </EnableFor>
      </div>
      <div className='alerts' />
      <div className='bottom'>
        {annotationPermissions.canAnnotate(props.id).allowed && !annotationPermissions.canReview(props.id).allowed && (
          <Button
            onClick={() => {
              history.push(Home.Annotations.Create.withParams({ projectId: props.id, workspaceId: props.workspaceId }));
            }}
          >
            {t('start_annotation')}
          </Button>
        )}
        <EnableFor
          hasPermission={annotationPermissions.canReview(props.id)}
          hideWhenNoAccess
        >
          <Button
            onClick={() => {
              history.push(Home.Annotations.Review.withParams({ projectId: props.id, workspaceId: props.workspaceId }));
            }}
          >
            {t('start_review')}
          </Button>
        </EnableFor>
      </div>
    </div>
  );
});
